import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { useBeta } from '../../contexts/BetaContext';
import { FlaskConical } from 'lucide-react';

import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import OfflineDetector from '../OfflineDetector';
import { useIsMobile } from '../hooks/use-mobile';

const Layout = () => {
  const location = useLocation();
  const { t } = useTranslation();

  // Use the mobile detection hook for consistent behavior
  // This hook returns true for screens up to 1023px (mobile, tablet, small desktop)
  const isMobile = useIsMobile();

  // Retrieve previous mobileOpen state from localStorage to prevent it from resetting on navigation
  const [mobileOpen, setMobileOpen] = useState(() => {
    // Always default to false for mobile, false for desktop (mobile sidebar should never be open on desktop)
    return false;
  });

  const { darkMode } = useSelector((state) => state.ui);
  const { user } = useSelector((state) => state.auth);
  const { getCurrentThemeData } = useTheme();
  const { isBetaRoute } = useBeta();
  const isCurrentPageBeta = isBetaRoute(location.pathname);



  // Enhanced drawer toggle that also persists the state
  const handleDrawerToggle = () => {
    const newState = !mobileOpen;
    setMobileOpen(newState);
    // Persist the state in localStorage
    localStorage.setItem('sidebarOpen', newState.toString());
  };

  // Sidebar width for layout spacing
  const drawerWidth = 256; // Fixed: 256px = 64 * 4 (lg:w-64)

  // Get current theme data for background hue shift
  const themeData = getCurrentThemeData();

  // Create hue-shifted background based on primary color
  const getThemedBackground = () => {
    if (!themeData) return darkMode ? "bg-[#181b20]" : "bg-background";

    try {
      const colors = darkMode ? themeData.darkColors || themeData.colors : themeData.colors;
      const bgHex = colors.background.replace('#', '');
      const bgR = parseInt(bgHex.substr(0, 2), 16);
      const bgG = parseInt(bgHex.substr(2, 2), 16);
      const bgB = parseInt(bgHex.substr(4, 2), 16);

      const primaryHex = colors.primary.replace('#', '');
      const pR = parseInt(primaryHex.substr(0, 2), 16);
      const pG = parseInt(primaryHex.substr(2, 2), 16);
      const pB = parseInt(primaryHex.substr(4, 2), 16);

      // Blend a very subtle primary tint onto the actual background
      const blend = darkMode ? 0.04 : 0.02;
      const r = Math.round(bgR + (pR - bgR) * blend);
      const g = Math.round(bgG + (pG - bgG) * blend);
      const b = Math.round(bgB + (pB - bgB) * blend);

      return `rgb(${r}, ${g}, ${b})`;
    } catch (error) {
      console.error('Error creating themed background:', error);
      return darkMode ? "bg-[#181b20]" : "bg-background";
    }
  };

  // Store the current section in localStorage to maintain context across refreshes
  useEffect(() => {
    if (location.pathname.includes('/superadmin/')) {
      localStorage.setItem('currentSection', 'superadmin');
    } else if (location.pathname.includes('/admin/')) {
      localStorage.setItem('currentSection', 'admin');
    }
  }, [location.pathname]);

  // This ensures we have persistent sidebar state regardless of navigation
  useEffect(() => {
    // Save sidebar state on route change
    localStorage.setItem('sidebarOpen', mobileOpen.toString());
    // Set up listener for browser back/forward navigation
    const handlePopState = () => {
      // Restore sidebar state from localStorage
      const savedState = localStorage.getItem('sidebarOpen');
      if (savedState !== null) {
        setMobileOpen(savedState === 'true');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [mobileOpen, location.pathname]);

  // Handle window resize to properly manage mobile sidebar state
  useEffect(() => {
    const handleResize = () => {
      // If window is resized to desktop size, close mobile sidebar
      if (!isMobile && mobileOpen) {
        setMobileOpen(false);
        localStorage.setItem('sidebarOpen', 'false');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileOpen, isMobile]);

  const themedBg = getThemedBackground();

  return (
    <div
      className={cn(
        "flex min-h-screen layout-stable transition-colors duration-150",
        "text-foreground"
      )}
      style={{
        backgroundColor: typeof themedBg === 'string' && themedBg.startsWith('rgb') ? themedBg : undefined
      }}
    >
      <Header
        drawerWidth={drawerWidth}
        handleDrawerToggle={handleDrawerToggle}
      />
      <Sidebar
        drawerWidth={drawerWidth}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
      />
      <main className={cn(
        "flex-1 flex flex-col min-h-screen overflow-x-hidden layout-stable",
        "lg:ml-64" // Fixed: Account for sidebar width on desktop (64 * 4 = 256px)
      )}>
        {/* Main content area */}
        <div
          className={cn(
            "flex-1 flex flex-col w-full",
            "p-1 sm:p-2 md:p-3"
          )}
          style={{ marginTop: `calc(3.5rem + env(safe-area-inset-top, 0px))` }}
        >
          <OfflineDetector>
            {isCurrentPageBeta && (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 text-primary text-sm">
                <FlaskConical className="h-4 w-4 flex-shrink-0" />
                <span><strong>{t("beta.feature")}</strong> — {t("beta.message")}</span>
              </div>
            )}
            <Outlet />
          </OfflineDetector>
        </div>

        {/* Footer - positioned at bottom of main content */}
        <div className="mt-auto pt-4">
          <Footer />
        </div>
      </main>
    </div>
  );
};

export default Layout;
